/**
 * Gemeinsame Tokens für die Illustrationen der Immobilien-Schritte.
 *
 * Bisher lagen die Farben lokal in `DachPicker.tsx` (`ROOF_FILL`/`ROOF_STROKE`).
 * Da der Bauartklassen-Schritt dieselbe Bildsprache benutzt (gleiche Dach- und
 * Wandfarben), liegen sie hier zentral — sonst driften die beiden
 * aufeinanderfolgenden Schritte optisch auseinander. Die viewBox ist bewusst
 * **nicht** mehr überall dieselbe: die Dachform-Icons zeichnen weiter in
 * `0 0 100 80`, die Wandaufbau-Schnitte (`bauartSchnitt.tsx`) brauchen ein
 * quadratisches `0 0 120 120`, weil sie drei gestapelte Zonen zeigen.
 *
 * Hier stehen bewusst auch die **Typen** `WandSignatur`/`DachSignatur`: `enums.ts`
 * ordnet jeder Bauartklasse ihre Signaturen zu und wird auch außerhalb dieses
 * Ordners importiert (`immobilieContractStep/AllgemeinBlocks.tsx`). Lägen die Typen
 * in der Icon-Datei, müsste `enums.ts` eine `.tsx`-Komponentendatei importieren.
 */
import { BRAND } from "../risikoanalyseStep/tokens";

export const ILLU = {
  /** Dachfläche „hart" — Ton wie in den Dachform-Icons. */
  roofFill: "#a4b4c8",
  /** Kontur/Grundlinie aller Gebäude-Illustrationen. */
  roofStroke: "#3a4a60",
  /** Wandfläche (Mauerwerk, Tafeln, Putz). */
  wallFill: "#eef1f5",
  /** Wandkontur. */
  wallStroke: "#3a4a60",
  /**
   * Mauer-/Plattenfugen — leiser als die Kontur, aber bewusst nicht dezent:
   * bei `massiv` und `fertigUmmantelt` ist die Fuge das einzige Merkmal, das
   * die Wand von einer glatten Fläche unterscheidet. Sie trägt damit Bedeutung
   * und braucht die 3:1 aus WCAG 1.4.11 gegen `wallFill` (hier 3,2:1); der
   * vorherige Ton #b9c3d0 kam nur auf 1,6:1 und verschwand in der schmalen
   * Handy-Spalte.
   */
  jointStroke: "#7b8798",
  /** Glasfelder der Rasterfassade. */
  glassFill: "#cfe0ef",
  /** Weiche Dachung (Reet/Stroh) — im Formular ist Gold bereits die Hinweisfarbe. */
  thatchFill: BRAND.gold,
  /** Holzbauteile: Schwelle, Ständer, Riegel, Andreaskreuz. */
  timberStroke: "#8a5a2b",
  /** Gefach zwischen den Holzbauteilen. */
  gefachFill: "#f6f1e7",
  /**
   * Mineralische Beplankung der Fertighaus-Schnitte (FHG I/II).
   *
   * Eigener Ton, weil `wallFill` (#eef1f5) und `gefachFill` (#f6f1e7)
   * zueinander nur 1,01:1 stehen — zwei benachbarte Schichten in diesen beiden
   * Füllungen wären nicht zu trennen, und genau das macht FHG I gegen FHG II
   * unlesbar. Gegen `wallFill` (Dämmung im Kern) hält #b9c6d6 1,53:1; die
   * Schichtgrenze trägt deshalb zusätzlich eine Linie in `wallStroke` und die
   * Platte eine eigene Strichtextur (ebenfalls `wallStroke`, 5,2:1). Die Zahl
   * der Platten bleibt damit auch ohne Farbwahrnehmung abzählbar.
   */
  plateFill: "#b9c6d6",
  /**
   * Fläche geschnittener Holzbauteile (Schwelle, Rähm, Andreaskreuz).
   * `timberStroke` war bisher nur Linienfarbe; ein in der Schnittebene
   * liegendes Holzbauteil braucht eine Fläche. #dcbb8e hält gegen
   * `timberStroke` 3,23:1 — Kontur und Faserbögen bleiben damit
   * bedeutungstragend lesbar. Von `plateFill` trennt es über die
   * Blau-Gelb-Achse, die bei Deuteranopie/Protanopie erhalten bleibt.
   */
  timberFill: "#dcbb8e",
  /**
   * Vollquerschnitt der Stahlprofile (BAK II). Bewusst dunkel: gegen
   * `glassFill` sind es 4,08:1, damit die nur 4 Einheiten breiten Profile im
   * Glasfeld nicht verschwinden. (`jointStroke` scheidet auf Glas aus — 2,70:1.)
   */
  steelFill: "#5b6a80",
  /**
   * Sockel/Gründung unter allen sechs Wandschnitten. Eigener Ton statt
   * `roofFill`: Dach- und Sockelzone liegen im selben Bild übereinander,
   * gleiche Farbe würde die beiden Zonen verwechselbar machen. Die
   * Beton-Schraffur in `wallStroke` hält darauf 3,53:1.
   */
  sockelFill: "#9aa3ad",
  /**
   * Halmstriche, Firstkappe und Kontur der weichen Dachung. Der vorherige Ton
   * #b5791a hielt gegen `thatchFill` (BRAND.gold) nur 1,85:1 — die Halme sind
   * aber genau das Merkmal, an dem BAK IV/V erkannt werden, und brauchen die
   * 3:1 aus WCAG 1.4.11. #7a4a0a kommt auf 3,76:1. Der alte Ton ist mit den
   * Haus-Icons (`bauartIcons.tsx`) weggefallen, die ihn als einzige benutzten.
   */
  thatchTexture: "#7a4a0a",
  /**
   * Hintergrund des Illustrationskopfes in den Bauartklassen-Karten. Bewusst
   * kühl-neutral und **nicht** das Flieder #f5eef6: das ist im direkt folgenden
   * DachPicker die Hover-/Aktiv-Farbe. Läge es hier auf allen acht Karten,
   * sähen über beide Schritte hinweg alle Kacheln „ausgewählt" aus.
   */
  headerBg: "#f4f6f9",
};

/**
 * Wand-Signaturen der Bauartklassen-Karten. Die Zuordnung Klasse → Signatur
 * steht in `enums.ts` und folgt der VEMA-Tabelle „Bauartklassen und
 * Fertighausgruppen".
 */
export type WandSignatur =
  | "massiv"
  | "stahlglas"
  | "holz"
  | "fertigFeuerbestaendig"
  | "fertigUmmantelt"
  | "fertigOffen";

/** Dach-Signaturen: harte vs. weiche Dachung. */
export type DachSignatur = "hart" | "weich";
